import {
  ForYouFeedbackKind,
  ForYouFeedbackResponse,
  ForYouGeoPreference,
  ForYouGeoSearchType,
  ForYouResolvedGeoSearchType,
  ForYouRibbon,
  ForYouRibbonPlacesResponse,
  ForYouRibbonsResponse,
  ForYouRibbonState,
} from "@/types/for-you"
import type { TripAdviceDismissalReason } from "@/types/trip"

import { ForYouEvent } from "@/lib/for-you-events"
import type { AppDispatch } from "@/lib/storage/redux-store"
import { baseApi } from "./base-api"

const MAX_DREAMING = 2
// The server's layout (ForYou::Ribbons::TYPE_ORDER / MAX_PERSONAL).
const TYPE_ORDER = ["restaurant", "attraction", "event", "hotel"]
const MAX_PERSONAL: Record<string, number> = { restaurant: 3, attraction: 3 }
const typeRank = (type: string) => {
  const rank = TYPE_ORDER.indexOf(type)
  return rank < 0 ? TYPE_ORDER.length : rank
}

export type GetForYouRibbonsArgs = {
  locationRef?: string
  /** They chose the picker's location themselves, rather than the page seeding it. */
  locationUserSelected?: boolean
  /** They asked for what is around them ("Use current location"), not for a place. */
  nearMe?: boolean
  /** The page seeded from the IP city (no device fix, no profile home). */
  approximateSeeded?: boolean
}

export type GetForYouRibbonPlacesArgs = {
  ref: string
  locationRef: string
  /**
   * The word the page is showing, from the ribbons response the row came in
   * (geo_preference.geo_search_type). The server serves the cards that decision
   * dealt the row under that word; the raw location flags stay on the ribbons call.
   */
  geoSearchType?: ForYouResolvedGeoSearchType
}

export type UpdateForYouGeoPreferenceArgs = {
  locationRef: string
  locationUserSelected: boolean
  geoSearchType: ForYouGeoSearchType
}

function locationParams({
  locationRef,
  locationUserSelected,
  nearMe,
  approximateSeeded,
}: GetForYouRibbonsArgs) {
  if (!locationRef) return undefined
  return {
    location_entity_ref: locationRef,
    location_user_selected: locationUserSelected ?? false,
    near_me: nearMe ?? false,
    approximate_seeded: approximateSeeded ?? false,
  }
}

/**
 * Whether two asks for the page would decide it the same way: the flags the
 * server guesses the in/near word from, compared as sent (a missing flag goes
 * out as false), so a subscriber that leaves one off doesn't refetch the whole
 * decision against one that says false.
 */
export function sameSelection(
  a: GetForYouRibbonsArgs,
  b: GetForYouRibbonsArgs
) {
  return (
    !!a.locationUserSelected === !!b.locationUserSelected &&
    !!a.nearMe === !!b.nearMe &&
    !!a.approximateSeeded === !!b.approximateSeeded
  )
}

/**
 * The flags ride the request but stay out of the cache key: the socket patches
 * and optimistic updates address a location's entry by `{ locationRef }`
 * alone. A flip of one refetches in place instead.
 */
function selectionChanged({
  currentArg,
  previousArg,
}: {
  currentArg: GetForYouRibbonsArgs | undefined
  previousArg: GetForYouRibbonsArgs | undefined
}) {
  return (
    previousArg !== undefined &&
    currentArg !== undefined &&
    !sameSelection(currentArg, previousArg)
  )
}

/** A row's entry is keyed by ref and location; a new word from the decision refetches it in place. */
function wordChanged({
  currentArg,
  previousArg,
}: {
  currentArg: GetForYouRibbonPlacesArgs | undefined
  previousArg: GetForYouRibbonPlacesArgs | undefined
}) {
  return (
    previousArg !== undefined &&
    currentArg !== undefined &&
    currentArg.geoSearchType !== previousArg.geoSearchType
  )
}

/**
 * One shared mutation entry for the heading's in/near save, so the rows can
 * see it in flight (see useForYouRibbonSection) without being its caller.
 */
export const FOR_YOU_GEO_PREFERENCE_SAVE = "for-you-geo-preference"

export type SendRibbonFeedbackArgs = {
  ref: string
  locationRef: string
  kind: ForYouFeedbackKind
  reason?: TripAdviceDismissalReason
  note?: string
}

function findRibbon(draft: ForYouRibbonsResponse, ref: string) {
  return (
    draft.here.find((r) => r.ref === ref) ??
    draft.dreaming.find((r) => r.ref === ref)
  )
}

function setRibbonState(
  draft: ForYouRibbonsResponse,
  ref: string,
  state: ForYouRibbonState
) {
  const ribbon = findRibbon(draft, ref)
  if (ribbon) ribbon.state = state
}

/**
 * A row landing from the agent joins its entity type's rows, keeping the
 * server's layout: it takes the type's lone default for events and stays,
 * and otherwise sits after their other rows of that type, ahead of its
 * best-of. A type already showing its cap waits for the next load's pick.
 * Location rows go to the ideas section.
 */
/** Under a row of theirs, the type's default is the fixed second helping ("More eats"), as the server sends it. */
function demoteToBestOf(row: ForYouRibbon) {
  if (row.kind !== "default") return
  row.kind = "fixed"
  row.title = `More ${row.title.toLowerCase()}`
}

function upsertRibbon(draft: ForYouRibbonsResponse, ribbon: ForYouRibbon) {
  const landed: ForYouRibbon = { ...ribbon, state: "ready" }
  const type = ribbon.entity_type
  if (type === "location") {
    draft.dreaming = [
      landed,
      ...draft.dreaming.filter((r) => r.ref !== ribbon.ref),
    ].slice(0, MAX_DREAMING)
    return
  }
  const here = draft.here.filter((r) => r.ref !== ribbon.ref)
  const ofType = here.filter((r) => r.entity_type === type)
  const personal = ofType.filter((r) => r.kind === "personal").length
  if (personal >= (MAX_PERSONAL[type] ?? 1)) return

  const defaultIndex = here.findIndex(
    (r) => r.kind === "default" && r.entity_type === type
  )
  if (defaultIndex >= 0 && !(type in MAX_PERSONAL)) {
    const previous = here[defaultIndex]
    here[defaultIndex] = {
      ...landed,
      slot_key: previous.slot_key ?? previous.ref,
    }
  } else {
    const bestOf = here.findIndex(
      (r) => r.entity_type === type && r.kind !== "personal"
    )
    const nextType = here.findIndex(
      (r) => typeRank(r.entity_type) > typeRank(type)
    )
    const at = bestOf >= 0 ? bestOf : nextType
    if (bestOf >= 0) demoteToBestOf(here[bestOf])
    here.splice(at >= 0 ? at : here.length, 0, landed)
  }
  draft.here = here
}

/** Patch the cached ribbon list for one socket event; rows never flash. */
export function applyForYouEvent(
  dispatch: AppDispatch,
  locationRef: string,
  event: ForYouEvent
) {
  dispatch(
    forYouApi.util.updateQueryData(
      "getForYouRibbons",
      { locationRef },
      (draft) => {
        switch (event.event) {
          // A run only flips idle default rows into "building" and back: a row
          // the user explicitly edited keeps its "updating" pill (that is the
          // louder, row-specific promise) until its replacement lands.
          case "run_started":
            draft.run.status = "running"
            draft.here.forEach((r) => {
              if (r.kind === "default" && r.state === "ready")
                r.state = "building"
            })
            break
          case "run_finished":
            draft.run.status = "idle"
            draft.here.forEach((r) => {
              if (r.state === "building") r.state = "ready"
            })
            break
          case "ribbon_added":
            upsertRibbon(draft, event.ribbon)
            break
          case "ribbon_replaced": {
            const index = event.replaces_ref
              ? draft.here.findIndex((r) => r.ref === event.replaces_ref)
              : -1
            if (index >= 0) {
              const previous = draft.here[index]
              // A restaurant or attraction default stays on as the best-of under the new row.
              if (
                previous.kind === "default" &&
                previous.entity_type in MAX_PERSONAL
              ) {
                demoteToBestOf(previous)
                draft.here.splice(index, 0, { ...event.ribbon, state: "ready" })
              } else {
                draft.here[index] = {
                  ...event.ribbon,
                  state: "ready",
                  slot_key: previous.slot_key ?? previous.ref,
                }
              }
            } else {
              upsertRibbon(draft, event.ribbon)
            }
            break
          }
          case "ribbon_retired":
            setRibbonState(draft, event.ref, "dismissed")
            break
          case "ribbon_updating":
            setRibbonState(draft, event.ref, "updating")
            break
          case "feedback_closed": {
            if (!event.ref) break
            const ribbon = findRibbon(draft, event.ref)
            if (ribbon) {
              ribbon.state = "ready"
              ribbon.notice = event.message
            }
            break
          }
        }
      }
    )
  )
}

/** The notice a feedback_closed event put on a row has been shown long enough. */
export function clearForYouNotice(
  dispatch: AppDispatch,
  locationRef: string,
  ref: string
) {
  dispatch(
    forYouApi.util.updateQueryData(
      "getForYouRibbons",
      { locationRef },
      (draft) => {
        const ribbon = findRibbon(draft, ref)
        if (ribbon) delete ribbon.notice
      }
    )
  )
}

export const forYouApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Headers only, in final order — fast. Each row loads its own places.
    getForYouRibbons: builder.query<
      ForYouRibbonsResponse,
      GetForYouRibbonsArgs
    >({
      query: (args) => ({
        url: "/api/for_you/ribbons",
        params: locationParams(args),
      }),
      serializeQueryArgs: ({ queryArgs }) => ({
        locationRef: queryArgs.locationRef,
      }),
      forceRefetch: selectionChanged,
      providesTags: ["ForYouRibbons"],
    }),
    getForYouRibbonPlaces: builder.query<
      ForYouRibbonPlacesResponse,
      GetForYouRibbonPlacesArgs
    >({
      query: ({ ref, locationRef, geoSearchType }) => ({
        url: `/api/for_you/ribbons/${ref}/places`,
        params: {
          location_entity_ref: locationRef,
          geo_search_type: geoSearchType,
        },
      }),
      serializeQueryArgs: ({ queryArgs }) => ({
        ref: queryArgs.ref,
        locationRef: queryArgs.locationRef,
      }),
      forceRefetch: wordChanged,
      providesTags: ["ForYouRibbonPlaces"],
    }),
    // The heading's in/near switch. The word flips at once; every row then
    // replays over the ground they chose.
    updateForYouGeoPreference: builder.mutation<
      ForYouGeoPreference,
      UpdateForYouGeoPreferenceArgs
    >({
      query: ({ geoSearchType, ...location }) => ({
        url: "/api/for_you/geo_preference",
        method: "PUT",
        params: locationParams(location),
        data: { geo_search_type: geoSearchType },
      }),
      invalidatesTags: ["ForYouRibbons", "ForYouRibbonPlaces"],
      async onQueryStarted(
        { locationRef, geoSearchType },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          forYouApi.util.updateQueryData(
            "getForYouRibbons",
            { locationRef },
            (draft) => {
              if (draft.geo_preference) {
                draft.geo_preference.geo_search_type = geoSearchType
                draft.geo_preference.saved = true
              }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
    refreshForYou: builder.mutation<
      { session_id?: number; skipped?: string },
      void
    >({
      query: () => ({ url: "/api/for_you/refresh", method: "POST" }),
    }),
    // Edit or dismiss one row. The row's state flips optimistically; the
    // socket's ribbon_replaced / ribbon_retired settles it.
    sendRibbonFeedback: builder.mutation<
      ForYouFeedbackResponse,
      SendRibbonFeedbackArgs
    >({
      query: ({ ref, kind, reason, note }) => ({
        url: `/api/for_you/ribbons/${ref}/feedback`,
        method: "POST",
        data: { kind, reason, note },
      }),
      async onQueryStarted(
        { ref, locationRef, kind, reason },
        { dispatch, queryFulfilled }
      ) {
        const state: ForYouRibbonState =
          kind === "edit" || reason === "other" ? "updating" : "dismissed"
        const patch = dispatch(
          forYouApi.util.updateQueryData(
            "getForYouRibbons",
            { locationRef },
            (draft) => setRibbonState(draft, ref, state)
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
    // The page-bottom box: freeform, no subject.
    sendForYouFeedback: builder.mutation<
      ForYouFeedbackResponse,
      { note: string }
    >({
      query: ({ note }) => ({
        url: "/api/for_you/feedback",
        method: "POST",
        data: { note },
      }),
    }),
  }),
})

export const {
  useGetForYouRibbonsQuery,
  useGetForYouRibbonPlacesQuery,
  useRefreshForYouMutation,
  useUpdateForYouGeoPreferenceMutation,
  useSendRibbonFeedbackMutation,
  useSendForYouFeedbackMutation,
} = forYouApi
